import type {CodeSectionLookup} from '@github-ui/code-nav'
import {noop} from '@github-ui/noop'
import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Box} from '@primer/styled-react'

import {useCodeLineIntersectionObservers} from '../../../../hooks/use-code-line-observer'
import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {type SharedLineObserverHandle, useRegisterLineObserver} from '../../../../hooks/use-shared-line-observer'
import {useStickyHeaderHeight} from '../../../../hooks/use-sticky-header-height'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {getActualLineNumberBinarySearch} from '../../../../utilities/lines'
import type {CodeLineData} from './hooks/use-code-lines'

export function StickyLineObserverOverlay({
  linesData,
  sharedLineObserver,
  onLineStickOrUnstick,
  codeLineToSectionMap,
}: {
  linesData: CodeLineData[]
  sharedLineObserver?: SharedLineObserverHandle
  onLineStickOrUnstick?: SetStickyLinesType
  codeLineToSectionMap?: CodeSectionLookup
}) {
  const stickyHeaderHeight = useStickyHeaderHeight()
  const lineHeight = useCurrentLineHeight('react-line-numbers')
  if (codeLineToSectionMap === undefined) return null

  return (
    <>
      {linesData.map(lineData => {
        const lineNumber = lineData.lineNumber
        const numParents = codeLineToSectionMap?.get(lineNumber)?.length ?? 0
        if (lineNumber === undefined || lineData.ownedSection === undefined) return null
        const actualLineNumber = getActualLineNumberBinarySearch(lineNumber, linesData)

        return (
          <StickyLineObserverLine
            key={`observer-overlay-${actualLineNumber}-lineNumber-no-virtualization`}
            className="symbol-highlight react-code-text"
            lineData={linesData[actualLineNumber]!}
            sharedLineObserver={sharedLineObserver}
            stickyHeaderHeight={stickyHeaderHeight}
            onLineStickOrUnstick={onLineStickOrUnstick ?? noop}
            numParents={numParents}
            sx={{
              position: 'absolute',
              top: lineHeight * (actualLineNumber - 1),
              pl: '10px',
              height: lineHeight,
              whiteSpace: 'pre',
            }}
          />
        )
      })}
    </>
  )
}

function StickyLineObserverLine({
  lineData,
  sharedLineObserver,
  stickyHeaderHeight,
  onLineStickOrUnstick,
  numParents,
  className,
  sx,
}: {
  lineData: CodeLineData
  sharedLineObserver?: SharedLineObserverHandle
  stickyHeaderHeight: number
  onLineStickOrUnstick: SetStickyLinesType
  numParents: number
  className: string
  sx: BetterSystemStyleObject
}) {
  // Shared observer path (feature flag: code_view_shared_line_observer)
  const sharedRefCallback = useRegisterLineObserver(sharedLineObserver, lineData, numParents)

  // Legacy per-line observer path (fallback when flag is off)
  const legacyRefCallback = useCodeLineIntersectionObservers(
    lineData,
    !sharedLineObserver,
    stickyHeaderHeight,
    onLineStickOrUnstick,
    numParents,
  )

  const lineRefCallbackForObserver = sharedLineObserver ? sharedRefCallback : legacyRefCallback
  return (
    <Box
      ref={ref => {
        lineRefCallbackForObserver(ref as HTMLTableRowElement | null)
      }}
      className={className}
      sx={{
        mb: '-20px',
        color: 'transparent',
        position: 'absolute',
        maxHeight: '6rem',
        overflow: 'hidden',
        width: '100%',
        display: 'inline-block',
        userSelect: 'none',
        ...sx,
      }}
      data-testid={'sticky-line-observer'}
    />
  )
}
